import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Radio,
  CircularProgress,
  LinearProgress,
  Stack,
  useTheme,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ReplayIcon from "@mui/icons-material/Replay";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../api/axiosInstance";

export default function QuizView({ subject, moduleId }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
    accent: "#A380FF",
    success: "#00e676",
    error: "#ff1744",
  };

  // Fetch quiz questions (includes student-specific answers if already completed)
  const fetchQuestions = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axiosInstance.get(`/quiz/questions?subject=${subject}&module_id=${moduleId}`);
      setQuestions(res.data);

      // Check if user has already submitted (Backend returns QuizQuestionWithUserAnswerDisplay)
      const firstQuestion = res.data[0];
      if (firstQuestion && firstQuestion.user_answer !== undefined && firstQuestion.user_answer !== null) {
        // Build answer dictionary
        const answers = {};
        let score = 0;
        res.data.forEach((q, idx) => {
          if (q.user_answer) {
            answers[idx] = q.user_answer;
            if (q.is_correct) {
              score += 1;
            }
          }
        });
        setSelectedAnswers(answers);
        setQuizResult({
          score: score,
          total: res.data.length,
        });
        setSubmitted(true);
        setQuizStarted(true);
      } else {
        setSubmitted(false);
        setQuizStarted(false);
        setSelectedAnswers({});
        setQuizResult(null);
      }
    } catch (err) {
      console.error("Error fetching quiz questions:", err);
      setErrorMsg("Failed to load quiz. Please sign in or try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, moduleId, user]);

  // Handle option selection
  const handleSelectOption = (option) => {
    if (submitted) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentIdx]: option,
    }));
  };

  // Submit quiz answers to backend
  const handleSubmitQuiz = async () => {
    // Validate that all questions are answered
    if (Object.keys(selectedAnswers).length < questions.length) {
      alert("Please answer all questions before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        subject: subject,
        module_id: moduleId,
        answers: questions.map((q, idx) => ({
          question_id: q.id,
          selected_option: selectedAnswers[idx],
        })),
      };

      const res = await axiosInstance.post("/quiz/submit", payload);
      setQuizResult(res.data);
      setSubmitted(true);

      // Re-fetch questions to populate correct answer details and explanations
      const updatedRes = await axiosInstance.get(`/quiz/questions?subject=${subject}&module_id=${moduleId}`);
      setQuestions(updatedRes.data);
    } catch (err) {
      console.error("Error submitting quiz:", err);
      alert("Failed to submit quiz. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset/retake quiz
  const handleResetQuiz = async () => {
    if (!window.confirm("Are you sure you want to reset and retake this quiz?")) return;
    setLoading(true);
    try {
      await axiosInstance.delete(`/quiz/reset?subject=${subject}&module_id=${moduleId}`);
      setSubmitted(false);
      setQuizStarted(false);
      setCurrentIdx(0);
      setSelectedAnswers({});
      setQuizResult(null);
      await fetchQuestions();
    } catch (err) {
      console.error("Error resetting quiz:", err);
      alert("Failed to reset quiz.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
        <CircularProgress size={50} sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Alert severity="error" variant="outlined" sx={{ borderRadius: "12px", border: `1px solid ${colors.error}` }}>
        {errorMsg}
      </Alert>
    );
  }

  if (questions.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: "center",
          borderRadius: "24px",
          border: `1px dashed ${theme.palette.divider}`,
          background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="text.secondary" gutterBottom>
          📂 Practice Quiz Coming Soon
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No quiz questions have been uploaded for Module {moduleId} yet.
        </Typography>
      </Paper>
    );
  }

  // 0️⃣ Lock Screen if not logged in
  if (!user) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, md: 6 },
          borderRadius: "24px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          background: isDark
            ? "linear-gradient(135deg, rgba(20,20,30,0.95), rgba(10,10,15,0.95))"
            : "linear-gradient(135deg, #ffffff, #fcfcfc)",
          backdropFilter: "blur(20px)",
          boxShadow: isDark ? "0 10px 40px rgba(0,0,0,0.4)" : "0 10px 40px rgba(0,0,0,0.05)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
            border: `1px solid ${colors.primary}40`,
          }}
        >
          <Typography variant="h4">🔒</Typography>
        </Box>

        <Typography variant="h4" fontWeight={900} gutterBottom sx={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
        }}>
          Practice Quiz Locked
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: "450px", margin: "0 auto 32px" }}>
          Please sign in to test your knowledge with the Module {moduleId} Quiz and track your learning journey.
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center" sx={{ flexWrap: "wrap", gap: 2 }}>
          <Button
            variant="contained"
            component={Link}
            to="/signin"
            state={{ from: location }}
            sx={{
              borderRadius: "12px",
              px: 5,
              py: 1.5,
              fontWeight: 700,
              textTransform: "none",
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              boxShadow: `0 8px 20px ${colors.primary}40`,
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 12px 25px ${colors.primary}60`,
              },
            }}
          >
            Sign In to Unlock
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to="/signup"
            sx={{
              borderRadius: "12px",
              px: 5,
              py: 1.5,
              fontWeight: 700,
              textTransform: "none",
              borderColor: colors.primary,
              color: colors.primary,
              "&:hover": {
                borderColor: colors.primary,
                background: `${colors.primary}10`,
              },
            }}
          >
            Create Free Account
          </Button>
        </Stack>
      </Paper>
    );
  }

  // 1️⃣ Intro / Welcome Screen
  if (!quizStarted) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, md: 6 },
          borderRadius: "24px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          background: isDark
            ? "linear-gradient(135deg, rgba(20,20,30,0.95), rgba(10,10,15,0.95))"
            : "linear-gradient(135deg, #ffffff, #fcfcfc)",
          backdropFilter: "blur(20px)",
          boxShadow: isDark ? "0 10px 40px rgba(0,0,0,0.4)" : "0 10px 40px rgba(0,0,0,0.05)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 70,
            height: 70,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
            border: `1px solid ${colors.primary}40`,
          }}
        >
          <Typography variant="h4">📝</Typography>
        </Box>

        <Typography variant="h4" fontWeight={900} gutterBottom>
          Module {moduleId} Quiz
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, maxWidth: "500px", margin: "0 auto 32px" }}>
          Test your understanding of the concepts in Module {moduleId}. Answer all multiple-choice questions to complete the module.
        </Typography>

        <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 4 }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color={colors.primary}>
              {questions.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">Questions</Typography>
          </Box>
          <Box sx={{ borderLeft: `1px solid ${theme.palette.divider}`, pl: 3 }} />
          <Box>
            <Typography variant="h6" fontWeight={800} color={colors.secondary}>
              100%
            </Typography>
            <Typography variant="caption" color="text.secondary">Passing Score</Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          size="large"
          onClick={() => setQuizStarted(true)}
          startIcon={<PlayArrowIcon />}
          sx={{
            px: 6,
            py: 1.8,
            borderRadius: "14px",
            fontWeight: 800,
            textTransform: "none",
            fontSize: "1.05rem",
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            boxShadow: `0 10px 30px ${colors.primary}40`,
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 15px 35px ${colors.primary}60`,
            },
          }}
        >
          Start Practice Quiz
        </Button>
      </Paper>
    );
  }

  // 2️⃣ Results Screen
  if (submitted && quizResult) {
    const percentage = Math.round((quizResult.score / quizResult.total) * 100);
    const passed = percentage >= 80;

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: "24px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          background: isDark
            ? "rgba(20,20,30,0.6)"
            : "#ffffff",
          backdropFilter: "blur(20px)",
        }}
      >
        {/* Results Banner */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Box
            sx={{
              position: "relative",
              width: 140,
              height: 140,
              margin: "0 auto 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress
              variant="determinate"
              value={100}
              size={140}
              thickness={4}
              sx={{ color: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", position: "absolute" }}
            />
            <CircularProgress
              variant="determinate"
              value={percentage}
              size={140}
              thickness={4}
              sx={{
                color: passed ? colors.success : colors.error,
                strokeLinecap: "round",
                position: "absolute",
              }}
            />
            <Box>
              <Typography variant="h3" fontWeight={900}>
                {percentage}%
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                SCORE
              </Typography>
            </Box>
          </Box>

          <Typography variant="h4" fontWeight={900} gutterBottom>
            {passed ? "Excellent Job! 🎉" : "Keep Learning! 📚"}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            You scored <strong>{quizResult.score}</strong> out of <strong>{quizResult.total}</strong> questions correctly.
          </Typography>

          <Button
            variant="outlined"
            onClick={handleResetQuiz}
            startIcon={<ReplayIcon />}
            sx={{
              borderRadius: "12px",
              px: 4,
              py: 1.2,
              fontWeight: 800,
              textTransform: "none",
              borderColor: colors.primary,
              color: colors.primary,
              "&:hover": {
                borderColor: colors.primary,
                background: `${colors.primary}10`,
              },
            }}
          >
            Retake Quiz
          </Button>
        </Box>

        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 4 }} />

        {/* Breakdown List */}
        <Typography variant="h6" fontWeight={850} sx={{ mb: 3 }}>
          Question Breakdown
        </Typography>
        <Stack spacing={4}>
          {questions.map((q, idx) => {
            const isCorrect = q.is_correct;
            const userAns = q.user_answer;

            return (
              <Box
                key={q.id}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: `1px solid ${isCorrect ? `${colors.success}30` : `${colors.error}30`}`,
                  background: isCorrect
                    ? (isDark ? "rgba(0, 230, 118, 0.03)" : "rgba(0, 230, 118, 0.015)")
                    : (isDark ? "rgba(255, 23, 68, 0.03)" : "rgba(255, 23, 68, 0.015)"),
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                  {isCorrect ? (
                    <CheckCircleOutlineIcon sx={{ color: colors.success, mt: 0.2 }} />
                  ) : (
                    <HighlightOffIcon sx={{ color: colors.error, mt: 0.2 }} />
                  )}
                  <Typography variant="body1" fontWeight={700}>
                    {idx + 1}. {q.question_text}
                  </Typography>
                </Stack>

                {/* Grid of options */}
                <Stack spacing={1} sx={{ pl: 4, mb: 2 }}>
                  {[
                    { key: "A", val: q.option_a },
                    { key: "B", val: q.option_b },
                    { key: "C", val: q.option_c },
                    { key: "D", val: q.option_d },
                  ].map((opt) => {
                    const isSelected = userAns === opt.key;
                    const isCorrectOption = q.correct_option === opt.key;

                    let optBg = "transparent";
                    let optBorder = `1px solid ${theme.palette.divider}`;
                    let optColor = "text.primary";

                    if (isCorrectOption) {
                      optBg = isDark ? "rgba(0, 230, 118, 0.15)" : "rgba(0, 230, 118, 0.1)";
                      optBorder = `1px solid ${colors.success}`;
                      optColor = colors.success;
                    } else if (isSelected && !isCorrect) {
                      optBg = isDark ? "rgba(255, 23, 68, 0.15)" : "rgba(255, 23, 68, 0.1)";
                      optBorder = `1px solid ${colors.error}`;
                      optColor = colors.error;
                    }

                    return (
                      <Box
                        key={opt.key}
                        sx={{
                          p: 1.5,
                          borderRadius: "10px",
                          background: optBg,
                          border: optBorder,
                          color: optColor,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          fontSize: "0.9rem",
                          fontWeight: (isSelected || isCorrectOption) ? 700 : 500,
                        }}
                      >
                        <strong style={{ opacity: 0.8 }}>({opt.key})</strong> {opt.val}
                      </Box>
                    );
                  })}
                </Stack>

                {/* Explanation Box */}
                {q.explanation && (
                  <Box
                    sx={{
                      ml: 4,
                      p: 2,
                      borderRadius: "10px",
                      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                      borderLeft: `3px solid ${colors.accent}`,
                      display: "flex",
                      gap: 1.5,
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: colors.accent, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" fontWeight={800} color={colors.accent} sx={{ display: "block", mb: 0.5 }}>
                        EXPLANATION
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem", lineHeight: 1.5 }}>
                        {q.explanation}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    );
  }

  // 3️⃣ Active Question View (Step-by-step)
  const currentQuestion = questions[currentIdx];
  const selectedOpt = selectedAnswers[currentIdx];
  const progressPercent = Math.round(((currentIdx + 1) / questions.length) * 100);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        background: isDark
          ? "linear-gradient(135deg, rgba(20,20,30,0.8), rgba(10,10,15,0.8))"
          : "#ffffff",
        backdropFilter: "blur(20px)",
        boxShadow: isDark ? "0 10px 40px rgba(0,0,0,0.3)" : "0 10px 40px rgba(0,0,0,0.03)",
      }}
    >
      {/* Progress Section */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={850} color={colors.primary} sx={{ letterSpacing: 1.5 }}>
            QUESTION {currentIdx + 1} OF {questions.length}
          </Typography>
          <Typography variant="caption" fontWeight={800} color="text.secondary">
            {progressPercent}% Complete
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
            "& .MuiLinearProgress-bar": {
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
              borderRadius: 3,
            },
          }}
        />
      </Box>

      {/* Question Text */}
      <Typography variant="h5" fontWeight={800} sx={{ mb: 4, lineHeight: 1.4 }}>
        {currentQuestion.question_text}
      </Typography>

      {/* Options Cards */}
      <Stack spacing={2} sx={{ mb: 5 }}>
        {[
          { key: "A", val: currentQuestion.option_a },
          { key: "B", val: currentQuestion.option_b },
          { key: "C", val: currentQuestion.option_c },
          { key: "D", val: currentQuestion.option_d },
        ].map((opt) => {
          const isSelected = selectedOpt === opt.key;
          return (
            <Box
              key={opt.key}
              onClick={() => handleSelectOption(opt.key)}
              sx={{
                p: 2.2,
                borderRadius: "14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: `1px solid ${
                  isSelected
                    ? colors.primary
                    : isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)"
                }`,
                background: isSelected
                  ? isDark
                    ? "rgba(229, 9, 20, 0.08)"
                    : "rgba(229, 9, 20, 0.04)"
                  : "transparent",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: isSelected ? colors.primary : colors.accent,
                  transform: "translateX(8px)",
                  background: isSelected
                    ? undefined
                    : isDark
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.01)",
                },
              }}
            >
              <Radio
                checked={isSelected}
                value={opt.key}
                color="primary"
                sx={{
                  color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                  "&.Mui-checked": {
                    color: colors.primary,
                  },
                }}
              />
              <Typography variant="body1" fontWeight={isSelected ? 700 : 500}>
                <strong style={{ marginRight: "8px", opacity: 0.8 }}>({opt.key})</strong> {opt.val}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 3 }} />

      {/* Navigation Buttons */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          disabled={currentIdx === 0}
          onClick={() => setCurrentIdx((prev) => prev - 1)}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: "12px",
            px: 3,
            py: 1.2,
            fontWeight: 700,
            textTransform: "none",
            color: "text.primary",
            borderColor: theme.palette.divider,
            "&:hover": {
              borderColor: "text.primary",
              background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
            },
          }}
        >
          Previous
        </Button>

        {currentIdx < questions.length - 1 ? (
          <Button
            variant="contained"
            disabled={!selectedOpt}
            onClick={() => setCurrentIdx((prev) => prev + 1)}
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: "12px",
              px: 4,
              py: 1.2,
              fontWeight: 700,
              textTransform: "none",
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              boxShadow: `0 8px 20px ${colors.primary}20`,
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 12px 25px ${colors.primary}40`,
              },
            }}
          >
            Next Question
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!selectedOpt || submitting}
            onClick={handleSubmitQuiz}
            endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            sx={{
              borderRadius: "12px",
              px: 4,
              py: 1.2,
              fontWeight: 800,
              textTransform: "none",
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              boxShadow: `0 8px 20px ${colors.primary}40`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 15px 30px ${colors.primary}60`,
              },
            }}
          >
            Submit Quiz
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
